import React, { useEffect, useState } from 'react';

import type {
  UserDTO,
  SearchRequestDTO_userSearchRequestDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

import AddNewButton from '../../../components/common/AddButton';
import Button from '../../../components/common/Button';
import Card from '../../../components/common/Card';
import FormField from '../../../components/common/FormField';
import AdminLayout from '../../../components/layout/AdminLayout';
import UserTable from '../../../components/tables/UserTable';
import { useUserApi } from '../../../hooks/useUserApi';
import { useFileApi } from '../../../hooks/useFileApi';
import { confirmDelete } from '../../../utils/sweetAlertUtils';

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [userImages, setUserImages] = useState<{ [key: string]: string }>({});

  const [firstNameSearch, setFirstNameSearch] = useState('');
  const [lastNameSearch, setLastNameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Users', href: '#' },
  ];

  const { search, remove } = useUserApi();
  const { getFileUrl } = useFileApi();

  const fetchUserImages = async (users: UserDTO[]): Promise<void> => {
    const imageUrls: { [key: string]: string } = {};

    for (const user of users) {
      if (user.profilePicture) {
        try {
          const response = await getFileUrl('users', user.profilePicture);
          if (response && response.success && response.data) {
            imageUrls[user.id!] = response.data;
          }
        } catch (error) {
          console.error(`Error fetching image for user ${user.id}:`, error);
        }
      }
    }
    setUserImages(imageUrls);
  };

  const fetchUsers = async (
    page: number,
    size: number,
    filters = {},
  ): Promise<void> => {
    const searchRequest: SearchRequestDTO_userSearchRequestDTO = {
      filters: {
        firstName: firstNameSearch || '',
        lastName: lastNameSearch || '',
        email: emailSearch || '',
        phoneNumber: '',
        role: 'Admin',
        ...filters,
      },
      page,
      pageSize: size,
      sortBy: 'FirstName',
      sortDescending: false,
    };

    try {
      setIsLoading(true);
      const response = await search(searchRequest);
      if (response && response.success && response.data) {
        const totalRecords = response.data?.totalCount ?? 0;
        const userResults = (response.data?.results as UserDTO[]) || [];

        setUsers(userResults);
        setTotalRecords(totalRecords);
        setTotalPages(Math.ceil(totalRecords / size));

        await fetchUserImages(userResults);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers(1, pageSize);
  };

  const handleClearSearch = (): void => {
    setFirstNameSearch('');
    setLastNameSearch('');
    setEmailSearch('');
    fetchUsers(1, pageSize, {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    });
  };

  const handleDelete = async (dto: UserDTO): Promise<void> => {
    const confirmed = await confirmDelete();
    if (!confirmed) {
      return;
    }
    try {
      await remove(dto.id || '');
      await fetchUsers(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/users"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
    >
      <Card
        title="Manage Users"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/users/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <FormField
              id="firstNameSearch"
              label="First Name"
              value={firstNameSearch}
              onChange={(e) => setFirstNameSearch(e.target.value)}
              placeholder="Search by first name"
            />
            <FormField
              id="lastNameSearch"
              label="Last Name"
              value={lastNameSearch}
              onChange={(e) => setLastNameSearch(e.target.value)}
              placeholder="Search by last name"
            />
            <FormField
              id="emailSearch"
              label="Email"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              placeholder="Search by email"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <UserTable
          users={users}
          userImages={userImages}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          isLoading={isLoading}
          onEdit={(user) => navigate(`/admin/users/${user.id}`)}
          onDelete={handleDelete}
        />
      </Card>
    </AdminLayout>
  );
};

export default UsersList;
